import {
  Box,
  Button,
  Center,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import FsmLayout from '@components/layout/layout/DefaultLayout/fsm/FsmLayout';
import { Autocomplete, LoadScriptNext } from '@react-google-maps/api';
import Head from 'next/head';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import DrugSearch from '../components/mfnCampaign/DrugSearch';
import { ResultsCard } from '../components/mfnCampaign/ResultsCard';
import SuggestedDrugs from '../components/mfnCampaign/SuggestedDrugs';

// Interface for individual country price details, should match ResultsCard and API
interface CountryPriceDetail {
  countryName: string;
  countryCode: string;
  flag: string;
  price: number;
  isMfnPrice: boolean;
}

interface PriceData {
  drugName: string;
  dosageLabel: string;
  monthlyQuantity: number;
  usMonthlyPrice: number;
  mfnMonthlyPrice: number; // This is the overall MFN price value
  monthlySavings: number;
  monthlySavingsPercentage: number;
  priceDetails: CountryPriceDetail[]; // The detailed breakdown
}

interface Dosage {
  id: string;
  label: string;
  defaultQuantity: number;
  priceMultiplier: number;
}

interface Drug {
  id: string;
  name: string;
  dosages: Dosage[];
  selectedDosage?: Dosage;
  basePricing?: any;
}

// Google Maps API
const libraries: 'places'[] = ['places'];

export default function MostFavoredNationCalculator() {
  const [selectedDrug, setSelectedDrug] = useState<Drug | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [monthlyQuantity, setMonthlyQuantity] = useState<number>(30);
  const [showExitModal, setShowExitModal] = useState(false);
  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  const comparePricesTimerIdRef = useRef<NodeJS.Timeout | null>(null);
  const initialPageLoadTimerIdRef = useRef<NodeJS.Timeout | null>(null);
  const [isInitialPageLoadDelayOver, setIsInitialPageLoadDelayOver] = useState(false);
  const isInitialPageLoadDelayOverRef = useRef(isInitialPageLoadDelayOver);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [submissionSuccessMessage, setSubmissionSuccessMessage] = useState('');
  const [countdown, setCountdown] = useState(10);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Initial 5-second page load delay for popup
  useEffect(() => {
    initialPageLoadTimerIdRef.current = setTimeout(() => {
      setIsInitialPageLoadDelayOver(true);
      isInitialPageLoadDelayOverRef.current = true;
    }, 5000);

    return () => {
      if (initialPageLoadTimerIdRef.current) {
        clearTimeout(initialPageLoadTimerIdRef.current);
        initialPageLoadTimerIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    isInitialPageLoadDelayOverRef.current = isInitialPageLoadDelayOver;
  }, [isInitialPageLoadDelayOver]);

  useEffect(() => {
    const handleMouseOut = (e: MouseEvent) => {
      if (!isInitialPageLoadDelayOverRef.current) {
        return;
      }
      if (!e.relatedTarget) {
        const exitModalShown = window.sessionStorage.getItem('mfnExitModalShown');
        if (!exitModalShown) {
          setShowExitModal(true);
          window.sessionStorage.setItem('mfnExitModalShown', 'true');
          if (comparePricesTimerIdRef.current) {
            clearTimeout(comparePricesTimerIdRef.current);
            comparePricesTimerIdRef.current = null;
          }
        }
      }
    };

    const docElement = document.documentElement;
    docElement.addEventListener('mouseout', handleMouseOut);

    return () => {
      docElement.removeEventListener('mouseout', handleMouseOut);
    };
  }, [isInitialPageLoadDelayOverRef]);

  useEffect(() => {
    return () => {
      if (comparePricesTimerIdRef.current) {
        clearTimeout(comparePricesTimerIdRef.current);
        comparePricesTimerIdRef.current = null;
      }
    };
  }, []);

  const clearCountdown = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearCountdown();
    };
  }, []);

  const handleModalClose = () => {
    setShowExitModal(false);
    clearCountdown();
    setShowSuccessMessage(false);
    setCountdown(10);
  };

  const handleAddressSubmit = async () => {
    if (!address.trim()) {
      setAddressError('Please enter your address');
      return;
    }
    setIsSubmitting(true);
    setAddressError('');

    try {
      const response = await fetch('/api/notify-slack-mfn-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address, source: 'MFN Report Request' }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Submission failed' }));
        throw new Error(errorData.error || errorData.message || 'Failed to submit report request.');
      }

      setSubmissionSuccessMessage('Your request has been received! You will receive your report soon.');
      setShowSuccessMessage(true);
      setCountdown(10);

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearCountdown();
            setShowExitModal(false);
            setShowSuccessMessage(false);
            return 10;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    } catch (err) {
      setAddressError(err instanceof Error ? err.message : 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onAutocompleteLoad = (autocompleteInstance: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocompleteInstance;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.formatted_address) {
        setAddress(place.formatted_address);
        setAddressError('');
      } else if (place && place.name) {
        // Fallback if formatted_address is not available but name is
        setAddress(place.name);
        setAddressError('');
      }
    }
  };

  const suggestedDrugsList: Drug[] = [
    {
      id: 'ozempic',
      name: 'Ozempic',
      dosages: [{ id: '0.5mg_pen', label: '0.5mg/1mL Pen', defaultQuantity: 1, priceMultiplier: 1.0 }],
      basePricing: {
        USA: 995.0,
        Canada: 450.0,
        Europe: {
          UK: 125.0,
          Germany: 129.6,
          France: 97.2,
          Italy: 102.6,
          Spain: 91.8,
        },
        SouthAmerica: {
          Brazil: 240.0,
          Colombia: 175.0,
        },
        AustraliaNewZealand: {
          Australia: 134.0,
          NewZealand: 120.0,
        },
        Asia: {
          Japan: 189.0,
          SouthKorea: 247.5,
          China: 112.0,
        },
      },
    },
    {
      id: 'insulin',
      name: 'Insulin',
      dosages: [{ id: '100IU_vial', label: '100 IU/mL Vial (10mL)', defaultQuantity: 1, priceMultiplier: 1.0 }],
      basePricing: {
        USA: 30.0,
        Canada: 30.0,
        Europe: {
          UK: 12.5,
          Germany: 21.6,
          France: 21.6,
          Italy: 21.6,
          Spain: 21.6,
        },
        SouthAmerica: {
          Brazil: 30.0,
          Colombia: 75.0,
        },
        AustraliaNewZealand: {
          Australia: 33.5,
          NewZealand: 30.0,
        },
        Asia: {
          Japan: 31.5,
          SouthKorea: 34.0,
          China: 70.0,
        },
      },
    },
    {
      id: 'viagra',
      name: 'Viagra',
      dosages: [
        { id: '25mg', label: '25mg Tablet', defaultQuantity: 10, priceMultiplier: 1.0 },
        { id: '50mg', label: '50mg Tablet', defaultQuantity: 10, priceMultiplier: 2.0 },
        { id: '100mg', label: '100mg Tablet', defaultQuantity: 10, priceMultiplier: 3.0 },
      ],
      basePricing: {
        USA: 10.0,
        Canada: 15.0,
        Europe: {
          UK: 5.0,
          Germany: 5.4,
          France: 5.4,
          Italy: 5.4,
          Spain: 5.4,
        },
        SouthAmerica: {
          Brazil: 15.0,
          Colombia: 7.5,
        },
        AustraliaNewZealand: {
          Australia: 13.4,
          NewZealand: 12.0,
        },
        Asia: {
          Japan: 14.0,
          SouthKorea: 15.0,
          China: 28.0,
        },
      },
    },
    {
      id: 'albuterol',
      name: 'Albuterol',
      dosages: [{ id: '90mcg_inhaler', label: '90µg Inhaler (200 doses)', defaultQuantity: 1, priceMultiplier: 1.0 }],
      basePricing: {
        USA: 30.0,
        Canada: 11.25,
        Europe: { UK: 2.5, Germany: 4.32, France: 5.0, Italy: 5.0, Spain: 5.0 },
        SouthAmerica: { Brazil: 6.0, Colombia: 15.0 },
        AustraliaNewZealand: { Australia: 6.7, NewZealand: 3.0 },
        Asia: { Japan: 7.0, SouthKorea: 7.5, China: 7.0 },
      },
    },
  ];

  const calculatorUrl = 'https://fairsquaremedicare.com/most-favored-nation';
  const ogImageUrl = 'https://www.fairsquaremedicare.com/mfn-calculator/social-preview.png';

  const isFormValid = selectedDrug !== null && selectedDrug.selectedDosage !== undefined && monthlyQuantity > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (comparePricesTimerIdRef.current) {
      clearTimeout(comparePricesTimerIdRef.current);
      comparePricesTimerIdRef.current = null;
    }

    // Log values immediately before the critical IF statement
    const currentIsFormValid = isFormValid;
    const currentSelectedDrug = selectedDrug;
    const currentSelectedDosage = selectedDrug?.selectedDosage;

    if (currentIsFormValid && currentSelectedDrug && currentSelectedDosage) {
      comparePricesTimerIdRef.current = setTimeout(() => {
        if (!isInitialPageLoadDelayOverRef.current) {
          comparePricesTimerIdRef.current = null;
          return;
        }
        const exitModalShown = window.sessionStorage.getItem('mfnExitModalShown');
        if (!exitModalShown) {
          setShowExitModal(true);
          window.sessionStorage.setItem('mfnExitModalShown', 'true');
        }
        comparePricesTimerIdRef.current = null;
      }, 8000);
    }

    // The rest of the API call logic should only proceed if the form is actually valid for that.
    if (!currentIsFormValid || !currentSelectedDrug || !currentSelectedDosage) {
      return;
    }

    setLoading(true);
    setError(null);

    const drugId = currentSelectedDrug.id;
    const dosageId = currentSelectedDosage.id;

    try {
      const response = await fetch(
        `/api/compare?drugId=${encodeURIComponent(drugId)}&dosageId=${encodeURIComponent(
          dosageId
        )}&monthlyQuantity=${encodeURIComponent(monthlyQuantity)}`
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: 'Failed to fetch price comparison and could not parse error response' }));
        throw new Error(errorData.error || errorData.message || 'Failed to fetch price comparison');
      }

      const data = await response.json();
      setPriceData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDrugSelect = (drug: Drug | null) => {
    setSelectedDrug(drug);
    setPriceData(null);
    setError(null);
    if (drug && drug.selectedDosage) {
      setMonthlyQuantity(drug.selectedDosage.defaultQuantity || 30);
    } else {
      setMonthlyQuantity(30);
    }
  };

  const googleMapsApiKeyFromEnv = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY;

  return (
    <FsmLayout>
      <Head>
        <title>Trump's Drug Plan Savings Calculator - Fair Square Medicare</title>
        <meta
          name="description"
          content={`Compare U.S. prices for ${
            selectedDrug?.name || 'prescription drugs'
          } with what other countries pay. See your potential savings with our Most Favored Nation (MFN) calculator.`}
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={calculatorUrl} />
        <meta
          property="og:title"
          content={`MFN Rx Savings for ${selectedDrug?.name || 'Drugs'}: See How the U.S. Compares!`}
        />
        <meta
          property="og:description"
          content={`Use our free tool to compare U.S. prices for ${
            selectedDrug?.name || 'prescription drugs'
          } to those in other developed nations. Understand your potential savings. #FairDrugPrices for Americans!`}
        />
        <meta property="og:image" content={ogImageUrl} /> {/* TODO: Create and host this image */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={calculatorUrl} />
        <meta
          property="twitter:title"
          content={`MFN Rx Savings for ${selectedDrug?.name || 'Drugs'}: See How the U.S. Compares!`}
        />
        <meta
          property="twitter:description"
          content={`Use our free tool to compare U.S. prices for ${
            selectedDrug?.name || 'prescription drugs'
          } to those in other developed nations. #FairDrugPrices for Americans!`}
        />
        <meta property="twitter:image" content={ogImageUrl} />
      </Head>
      <VStack spacing={10} align="stretch" maxW="1200px" mx="auto" py={10} px={[4, 6, 8]}>
        <Box width="100%" p={{ base: 0, md: 8 }} bg="blue.50" borderRadius="lg" overflow="hidden">
          <Heading as="h1" size="2xl" mb={4} textAlign="center" color="blue.800">
            Global Rx Price Check
          </Heading>
          <Text textAlign="center" color="blue.700" fontSize="xl" mb={2}>
            See how much Trump&apos;s drug plan could save you for any Rx
          </Text>
          <Text textAlign="center" color="blue.700" fontSize="sm" mb={8}>
            <sup>*</sup>Prices are approximate and may vary
          </Text>

          <Box bg="white" rounded="lg" shadow="lg" p={8}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={6}>
                <DrugSearch
                  onDrugSelect={handleDrugSelect}
                  error={
                    !selectedDrug
                      ? 'Please select a drug'
                      : !selectedDrug.selectedDosage
                      ? 'Please select a dosage'
                      : undefined
                  }
                  externallySelectedDrug={selectedDrug}
                />
                <SuggestedDrugs suggestedDrugs={suggestedDrugsList} onDrugSelect={handleDrugSelect} />

                {selectedDrug && selectedDrug.selectedDosage && (
                  <FormControl id="monthlyQuantity">
                    <FormLabel>
                      Monthly Quantity (number of{' '}
                      {selectedDrug.selectedDosage.label.toLowerCase().includes('inhaler') ||
                      selectedDrug.selectedDosage.label.toLowerCase().includes('pen')
                        ? selectedDrug.selectedDosage.label.split(' ')[1] || 'units'
                        : selectedDrug.selectedDosage.label.split(' ')[1]?.replace(/s$/, '') || 'units'}
                      {selectedDrug.selectedDosage.defaultQuantity === 1 ? '' : 's'})
                    </FormLabel>
                    <NumberInput
                      value={monthlyQuantity}
                      onChange={(_, valueAsNumber) => setMonthlyQuantity(isNaN(valueAsNumber) ? 0 : valueAsNumber)}
                      min={1}
                      max={1000}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                )}

                <Button
                  type="submit"
                  colorScheme="purple"
                  size="lg"
                  width="full"
                  isDisabled={!isFormValid || loading}
                  isLoading={loading}
                  loadingText="Fetching prices..."
                >
                  Compare Prices
                </Button>
              </VStack>
            </form>

            {error && (
              <Center mt={6}>
                <Text color="red.500">{error}</Text>
              </Center>
            )}

            {priceData && !loading && !error && (
              <Box mt={8}>
                <ResultsCard
                  drugName={priceData.drugName}
                  dosageLabel={priceData.dosageLabel}
                  monthlyQuantity={priceData.monthlyQuantity}
                  usMonthlyPrice={priceData.usMonthlyPrice}
                  mfnMonthlyPriceOverall={priceData.mfnMonthlyPrice}
                  monthlySavingsOverall={priceData.monthlySavings}
                  monthlySavingsPercentageOverall={priceData.monthlySavingsPercentage}
                  priceDetails={priceData.priceDetails}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Divider />
      </VStack>

      <Modal isOpen={showExitModal} onClose={handleModalClose} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent maxW="600px" p={6} mt="20vh">
          <ModalHeader>
            <Heading size="lg" color="blue.800">
              Get Your Free Medicare 101 Report 🇺🇸
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {showSuccessMessage ? (
              <VStack spacing={4} textAlign="center" py={8}>
                <Text fontSize="xl" color="green.500" fontWeight="bold">
                  Success!
                </Text>
                <Text fontSize="lg">{submissionSuccessMessage}</Text>
                <Text fontSize="md" color="gray.600">
                  Closing in {countdown} second{countdown === 1 ? '' : 's'}...
                </Text>
                <Spinner color="green.500" />
              </VStack>
            ) : (
              <>
                <Text mb={4} fontSize="xl">
                  Get your free Medicare report delivered today!{' '}
                  <strong>Learn everything you need to know about Medicare before your 65th birthday.</strong>
                </Text>

                <FormControl isInvalid={!!addressError} mb={4}>
                  <FormLabel fontWeight="bold">Your Mailing Address</FormLabel>
                  {googleMapsApiKeyFromEnv ? (
                    <LoadScriptNext googleMapsApiKey={googleMapsApiKeyFromEnv} libraries={libraries}>
                      <Autocomplete
                        onLoad={onAutocompleteLoad}
                        onPlaceChanged={onPlaceChanged}
                        options={{ types: ['address'] }}
                        fields={['formatted_address', 'name', 'geometry']}
                      >
                        <Input
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                            if (addressError) setAddressError('');
                          }}
                          placeholder="Enter your address to receive the report"
                          id="address-autocomplete-input"
                          isDisabled={isSubmitting}
                        />
                      </Autocomplete>
                    </LoadScriptNext>
                  ) : (
                    <Input
                      value={address}
                      onChange={(e) => {
                        setAddress(e.target.value);
                        if (addressError) setAddressError('');
                      }}
                      placeholder="Enter your address manually (autocomplete unavailable)"
                      id="address-manual-input"
                      isDisabled={isSubmitting}
                    />
                  )}
                  {!googleMapsApiKeyFromEnv && !addressError && (
                    <Text fontSize="xs" color="orange.500" mt={1}>
                      Address autocomplete is currently unavailable. Please type manually.
                    </Text>
                  )}
                  <FormErrorMessage>{addressError}</FormErrorMessage>
                </FormControl>

                <Button
                  colorScheme="red"
                  onClick={handleAddressSubmit}
                  w="full"
                  isLoading={isSubmitting}
                  loadingText="Submitting..."
                  mb={4}
                >
                  Send Me The Report
                </Button>

                <Image
                  src="/mfn-calculator/medicare-101.png"
                  alt="Medicare 101 Report Cover"
                  width={500}
                  height={200}
                  style={{ borderRadius: '10px', marginTop: '20px' }}
                />
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </FsmLayout>
  );
}
